import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import LegalEntityDisclosure from "../components/LegalEntityDisclosure";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Sparkles, Crown, ArrowRight, ShieldCheck, Lock } from "lucide-react";
import RevealOnScroll from "../components/RevealOnScroll";
import { PioneerAvailabilityBadge } from "../components/PioneerAvailabilityBadge";

/**
 * Public Pioneer Wall — social proof showcase of VIP Capital Club Pioneers
 * (users who joined with a YACHT-style promo code).
 *
 * Drives FOMO via: (1) numbered cards in cinematic gold, (2) live
 * "X of 100 seats claimed" counter, (3) a progress meter, and
 * (4) a strong CTA back to /register when seats remain.
 */
const PioneersPage = () => {
  const [data, setData] = useState({ pioneers: [], claimed: 0, total_seats: 100 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/pioneers/wall");
        if (!cancelled) setData(r.data || {});
      } catch (err) {
        // Public endpoint — log for debugging but surface empty state to user
        console.error("PioneersPage: failed to load wall", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Mount-only fetch — no reactive deps needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remaining = Math.max(0, (data.total_seats || 100) - (data.claimed || 0));
  const qualifyingOpen = remaining > 0;

  return (
    <div className="min-h-screen bg-obsidian text-cream" data-testid="pioneers-page">
      {/* Shared public nav (forge-20260609-003) — replaces the minimal
          logo-only header; the page keeps its own Claim CTA in the hero. */}
      <PublicNav active="pioneers" />

      {/* Hero */}
      <section className="relative px-8 pt-20 pb-12 overflow-hidden">
        {/* Faint gold radial */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 50% 0%, rgba(234,179,8,0.10) 0%, transparent 55%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold/10 border border-gold/30 text-gold text-[11px] uppercase tracking-[0.3em] mb-6">
            <Crown size={12} /> Wall of Champions
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-cream leading-tight">
            The Founding{" "}
            <span className="text-gold">Pioneers</span>
          </h1>
          <p className="mt-5 text-lg text-platinum/75 leading-relaxed max-w-2xl mx-auto">
            The first <span className="text-gold font-semibold">100 members</span> of the
            EchoSense VIP Capital Club. Their pioneer numbers are sealed for life —
            recognized, never re-issued, and rewarded with founding-member privileges.
          </p>

          {/* Qualifying availability — traffic-light indicator. Replaces
              the old "X of 100 seats claimed" numeric counter so we do
              not publicly disclose how many partners have onboarded. */}
          <div className="mt-10 flex justify-center" data-testid="pioneer-counter">
            <PioneerAvailabilityBadge claimed={data.claimed} />
          </div>

          {/* Hero CTAs */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" data-testid="pioneer-cta-claim">
              <Button className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold px-6 py-6 h-auto text-base">
                <Sparkles size={16} className="mr-2" />
                Claim Your Pioneer Seat
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/about" data-testid="pioneer-cta-learn">
              <Button
                variant="outline"
                className="border-platinum/30 text-platinum/85 hover:bg-platinum/10 px-6 py-6 h-auto text-base bg-transparent"
              >
                What Pioneer Status Unlocks
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Wall grid */}
      <section className="px-8 pb-24">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20 text-slate-500" data-testid="pioneer-loading">
              Loading the wall…
            </div>
          ) : data.pioneers.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <div className="flex items-center justify-between mb-6 px-1">
                <h2 className="text-lg font-serif text-platinum/85 tracking-wide">
                  Currently Sealed
                </h2>
                <p className="text-xs uppercase tracking-[0.25em] text-slate-500">
                  Identities masked for privacy
                </p>
              </div>
              <div
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4"
                data-testid="pioneer-grid"
              >
                {data.pioneers.map((p, i) => (
                  <RevealOnScroll
                    key={`pioneer-${p.pioneer_number}`}
                    delay={Math.min(i * 35, 600)}
                  >
                    <PioneerCard pioneer={p} />
                  </RevealOnScroll>
                ))}
              </div>
            </>
          )}

          {/* Trust strip */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Pillar
              icon={ShieldCheck}
              title="Compliance-First"
              copy="Every Pioneer cleared KYC/AML before being sealed onto the wall."
            />
            <Pillar
              icon={Lock}
              title="Privacy Protected"
              copy="Only first name + last initial appear publicly. Never emails or contact info."
            />
            <Pillar
              icon={Crown}
              title="Lifetime Benefits"
              copy="Locked-in $19.95/mo dues, founding-member badge, priority Deal Room access."
            />
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      {qualifyingOpen && (
        <section className="border-t border-platinum/10 bg-surface-highlight py-14 px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[11px] uppercase tracking-[0.35em] text-gold/90 font-semibold mb-3">
              Pioneer Wall
            </p>
            <h3 className="text-3xl md:text-4xl font-serif text-cream">
              Qualifying is still open.
            </h3>
            <p className="text-platinum/75 mt-3">
              When all 100 pioneer seats are claimed, the wall closes for life.
              Pioneer pricing and privileges are never re-offered.
            </p>
            <div className="mt-5 flex justify-center">
              <PioneerAvailabilityBadge claimed={data.claimed} compact />
            </div>
            <Link to="/register" data-testid="pioneer-footer-cta">
              <Button className="mt-6 bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold px-8 py-6 h-auto text-base">
                Reserve Your Pioneer Number
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* Footer — entity disclosure (SumSub requirement; this page lacked it)
          + the Disclaimer link relocated from the top bar (forge-20260609-004). */}
      <footer className="border-t border-platinum/10 py-8 px-6 mt-4">
        <div className="max-w-5xl mx-auto space-y-3">
          <LegalEntityDisclosure variant="footer" />
          <p className="text-platinum/40 text-xs text-center">
            © {new Date().getFullYear()} EchoSense Capital Club. All rights reserved.
            {' · '}
            <Link to="/disclaimer" className="hover:text-platinum underline-offset-2 hover:underline" data-testid="footer-disclaimer">Disclaimer</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

const PioneerCard = ({ pioneer }) => {
  const num = String(pioneer.pioneer_number || 0).padStart(3, "0");
  return (
    <div
      className="group relative rounded-lg border border-amber-400/20 bg-gradient-to-b from-amber-400/[0.06] to-transparent p-4 hover:border-amber-400/50 hover:from-amber-400/[0.10] transition-all duration-300"
      data-testid={`pioneer-card-${pioneer.pioneer_number}`}
    >
      <p className="text-[10px] uppercase tracking-[0.35em] text-gold/70 mb-1">
        Pioneer
      </p>
      <p className="text-3xl font-serif font-semibold tabular-nums leading-none text-gold">
        №{num}
      </p>
      <p className="mt-3 text-sm text-cream font-medium truncate">
        {pioneer.display_name}
      </p>
      {pioneer.joined && (
        <p className="text-[11px] text-slate-500 mt-0.5">Joined {pioneer.joined}</p>
      )}
      {/* Hover glow */}
      <div
        aria-hidden="true"
        className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{ boxShadow: "0 0 28px rgba(234,179,8,0.20) inset" }}
      />
    </div>
  );
};

const EmptyState = () => (
  <div
    className="text-center py-16 rounded-xl border border-amber-400/30 bg-amber-400/[0.05]"
    data-testid="pioneer-empty"
  >
    <Crown className="mx-auto text-gold mb-4" size={36} />
    <h3 className="text-2xl font-serif text-cream">Be Pioneer №001</h3>
    <p className="text-platinum/75 mt-2 max-w-md mx-auto">
      The wall is empty. The first 100 members of the EchoSense VIP Capital Club
      will be sealed here for life — the very first one will be you, if you
      choose it.
    </p>
    <Link to="/register" data-testid="pioneer-empty-cta">
      <Button className="mt-6 bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold">
        Found the Wall <ArrowRight size={14} className="ml-2" />
      </Button>
    </Link>
  </div>
);

const Pillar = ({ icon: Icon, title, copy }) => (
  <div className="rounded-lg border border-platinum/10 bg-surface-highlight/60 p-5">
    <Icon className="text-gold mb-2" size={20} />
    <p className="text-sm uppercase tracking-wider text-platinum/85 font-semibold mb-1">
      {title}
    </p>
    <p className="text-sm text-platinum/60 leading-relaxed">{copy}</p>
  </div>
);

export default PioneersPage;
